/* eslint-disable valid-typeof */
import React, { useState, useCallback, useEffect } from "react";
import { loadWeb3, getWeb3Instance } from "./Connectivity/Connectivity";
import { toast } from "react-toastify";
import {
  AxonftMarketplace,
  AxonftMarketplaceAbi,
  USDT,
  USDTAbi,
} from "./Contract";
import "bootstrap/dist/css/bootstrap.min.css";

export default function MarketplacePage() {
  const [btnTxt, setBtnTxt] = useState("Connect Wallet");
  const [account, setAccount] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingStates, setLoadingStates] = useState({});
  
  // Function inputs and outputs storage
  const [functionInputs, setFunctionInputs] = useState({});
  const [functionOutputs, setFunctionOutputs] = useState({});
  
  // Overview data
  const [marketplaceData, setMarketplaceData] = useState({
    totalNftsMinted: "",
    maxNftSupply: "",
    nftBasePrice: "",
    queueLength: "",
    isClaimEnabled: false,
    usdtBalance: "",
    userUsdtBalance: ""
  });

  // Marketplace contract functions definitions - All from ABI
  const marketplaceFunctions = {
    readFunctions: [
      // Basic Info
      {
        name: "adminWallet",
        title: "Admin Wallet",
        description: "Get the administrative wallet address",
        inputs: [],
        icon: "👑",
        category: "Basic Info"
      },
      {
        name: "burnFeeReceiver",
        title: "Burn Fee Receiver",
        description: "Get the burn fee receiver address",
        inputs: [],
        icon: "🔥",
        category: "Basic Info"
      },
      {
        name: "nftContract",
        title: "NFT Contract",
        description: "Get the NFT contract address",
        inputs: [],
        icon: "🎨",
        category: "Basic Info"
      },
      {
        name: "nftMatrixContract",
        title: "NFT Matrix Contract",
        description: "Get the NFT matrix contract address",
        inputs: [],
        icon: "🤖",
        category: "Basic Info"
      },
      {
        name: "nftBurnReward",
        title: "NFT Burn Reward",
        description: "Get the NFT burn reward contract address",
        inputs: [],
        icon: "🔥",
        category: "Basic Info"
      },
      {
        name: "nftSaleFundWallet",
        title: "NFT Sale Fund Wallet",
        description: "Get the NFT sale fund wallet address",
        inputs: [],
        icon: "🏦",
        category: "Basic Info"
      },
      {
        name: "topIdWallet",
        title: "Top ID Wallet",
        description: "Get the top ID wallet address",
        inputs: [],
        icon: "🏆",
        category: "Basic Info"
      },
      {
        name: "platformRewardReceiver",
        title: "Platform Reward Receiver",
        description: "Get the platform reward receiver address",
        inputs: [],
        icon: "📤",
        category: "Basic Info"
      },
      {
        name: "owner",
        title: "Owner",
        description: "Get the contract owner",
        inputs: [],
        icon: "👑",
        category: "Basic Info"
      },
      {
        name: "paused",
        title: "Paused",
        description: "Check if contract is paused",
        inputs: [],
        icon: "⏸️",
        category: "Basic Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "isClaimEnable",
        title: "Claim Status",
        description: "Check if claiming is enabled",
        inputs: [],
        icon: "✅",
        category: "Basic Info",
        formatOutput: (value) => value ? 'Enabled' : 'Disabled'
      },
      {
        name: "usdtToken",
        title: "USDT Token",
        description: "Get the USDT token address",
        inputs: [],
        icon: "💰",
        category: "Basic Info"
      },
      // Income
      {
        name: "buringIncomePercent",
        title: "Burning Income %",
        description: "Get the burning income percentage",
        inputs: [],
        icon: "🔥",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "levelIncomePercent",
        title: "Level Income %",
        description: "Get the level income percentage",
        inputs: [],
        icon: "📊",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "nftOwnerIncomePercent",
        title: "NFT Owner Income %",
        description: "Get the NFT owner income percentage",
        inputs: [],
        icon: "💰",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "platformIncomePercent",
        title: "Platform Income %",
        description: "Get the platform income percentage",
        inputs: [],
        icon: "🏢",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      // NFT Config
      {
        name: "maxNftSupply",
        title: "Max NFT Supply",
        description: "Get the maximum NFT supply",
        inputs: [],
        icon: "📈",
        category: "NFT Config"
      },
      {
        name: "mintNftCount",
        title: "Mint NFT Count",
        description: "Get the mint NFT count",
        inputs: [],
        icon: "🔢",
        category: "NFT Config"
      },
      {
        name: "nftBasePrice",
        title: "NFT Base Price",
        description: "Get the NFT base price",
        inputs: [],
        icon: "💵",
        category: "NFT Config"
      },
      {
        name: "nftBurnBasePrice",
        title: "NFT Burn Base Price",
        description: "Get the NFT burn base price",
        inputs: [],
        icon: "🔥",
        category: "NFT Config"
      },
      // Trading
      {
        name: "maxSalePrice",
        title: "Max Sale Price",
        description: "Get the maximum sale price",
        inputs: [],
        icon: "💲",
        category: "Trading"
      },
      {
        name: "nftSaleDuration",
        title: "NFT Sale Duration",
        description: "Get the NFT sale duration",
        inputs: [],
        icon: "⏱️",
        category: "Trading"
      },
      {
        name: "sellPercent",
        title: "Sell Percent",
        description: "Get the sell percent",
        inputs: [],
        icon: "📉",
        category: "Trading",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "getNftSaleDetails",
        title: "Get NFT Sale Details",
        description: "Get NFT sale details",
        inputs: [{ name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true, label: "Token ID" }],
        icon: "📊",
        category: "Trading",
        formatOutput: (value) => `Base Price: ${value[0]}\nCurrent Owner: ${value[1]}\nIs Sale: ${value[2] ? 'Yes' : 'No'}\nBuy Timestamp: ${value[3]}\nSale Timestamp: ${value[4]}`
      },
      {
        name: "nftOwner",
        title: "NFT Owner",
        description: "Get owner of specific NFT",
        inputs: [{ name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true, label: "Token ID" }],
        icon: "👤",
        category: "Trading"
      },
      {
        name: "nftSale",
        title: "NFT Sale",
        description: "Get NFT sale information",
        inputs: [{ name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true, label: "NFT ID" }],
        icon: "🏪",
        category: "Trading",
        formatOutput: (value) => `Base Price: ${value[0]}\nLast Timestamp: ${value[1]}\nOwner: ${value[2]}\nIs Sale: ${value[3] ? 'Yes' : 'No'}`
      },
      {
        name: "nftSalePrice",
        title: "NFT Sale Price",
        description: "Get sale price of NFT",
        inputs: [{ name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true, label: "NFT ID" }],
        icon: "💲",
        category: "Trading"
      },
      {
        name: "nftTimeInfo",
        title: "NFT Time Info",
        description: "Get NFT time information",
        inputs: [{ name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true, label: "NFT ID" }],
        icon: "⏰",
        category: "Trading",
        formatOutput: (value) => `Buy Timestamp: ${value[0]}\nSale Timestamp: ${value[1]}`
      },
      // Stats
      {
        name: "globalPeriod",
        title: "Global Period",
        description: "Get the global period",
        inputs: [],
        icon: "⏱️",
        category: "Stats"
      },
      {
        name: "totalNftsMinted",
        title: "Total NFTs Minted",
        description: "Get the total NFTs minted",
        inputs: [],
        icon: "🎨",
        category: "Stats"
      },
      // Auto Trade & Axo Functions
      {
        name: "autoTradeFee",
        title: "Auto Trade Fee",
        description: "Get the auto trade fee",
        inputs: [],
        icon: "💸",
        category: "Auto Trade"
      },
      {
        name: "autoTradeFeeReceiver",
        title: "Auto Trade Fee Receiver",
        description: "Get the auto trade fee receiver address",
        inputs: [],
        icon: "📥",
        category: "Auto Trade"
      },
      {
        name: "isAutoTradeFee",
        title: "Is Auto Trade Fee",
        description: "Check if the auto trade fee is enabled",
        inputs: [],
        icon: "❓",
        category: "Auto Trade",
        formatOutput: (value) => value ? 'Enabled' : 'Disabled'
      },
      {
        name: "axoInfo",
        title: "Axo Info",
        description: "Get Axo information for an address",
        inputs: [{ name: "account", type: "address", placeholder: "Enter Axo address", required: true, label: "Axo Address" }],
        icon: "🤖",
        category: "Auto Trade",
        formatOutput: (value) => `Last Timestamp: ${value[0]}\nIs Enabled: ${value[1] ? 'Yes' : 'No'}`
      },
      {
        name: "botCount",
        title: "Bot Count",
        description: "Get the total number of bots",
        inputs: [],
        icon: "🔢",
        category: "Auto Trade"
      },
      {
        name: "botById",
        title: "Bot By ID",
        description: "Get bot address by its ID",
        inputs: [{ name: "botId", type: "uint256", placeholder: "Enter bot ID", required: true, label: "Bot ID" }],
        icon: "🆔",
        category: "Auto Trade"
      },
      {
        name: "botInfo",
        title: "Bot Info",
        description: "Get bot information for an address",
        inputs: [{ name: "account", type: "address", placeholder: "Enter bot address", required: true, label: "Bot Address" }],
        icon: "🤖",
        category: "Auto Trade",
        formatOutput: (value) => `Last Timestamp: ${value[0]}\nBot ID: ${value[1]}\nIs Enabled: ${value[2] ? 'Yes' : 'No'}`
      },
      {
        name: "botTimeDuration",
        title: "Bot Time Duration",
        description: "Get the bot time duration",
        inputs: [],
        icon: "⏱️",
        category: "Auto Trade"
      },
      {
        name: "getEligibleTradeBots",
        title: "Get Eligible Trade Bots",
        description: "Get the list of eligible trade bots",
        inputs: [],
        icon: "📋",
        category: "Auto Trade",
        formatOutput: (value) => value.join(', ') || 'None'
      },
      {
        name: "tradeEnd",
        title: "Trade End",
        description: "Get the trade queue end index",
        inputs: [],
        icon: "⬇️",
        category: "Auto Trade"
      },
      {
        name: "tradeQueue",
        title: "Trade Queue",
        description: "Get trade queue address at index",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "📋",
        category: "Auto Trade"
      },
      {
        name: "tradeStart",
        title: "Trade Start",
        description: "Get the trade queue start index",
        inputs: [],
        icon: "⬆️",
        category: "Auto Trade"
      },
      // Queue Management
      {
        name: "nftQueueEnd",
        title: "NFT Queue End",
        description: "Get the NFT queue end",
        inputs: [],
        icon: "⬇️",
        category: "Queue Management"
      },
      {
        name: "nftQueueStart",
        title: "NFT Queue Start",
        description: "Get the NFT queue start",
        inputs: [],
        icon: "⬆️",
        category: "Queue Management"
      },
      {
        name: "nftQueueLength",
        title: "NFT Queue Length",
        description: "Get the NFT queue length",
        inputs: [],
        icon: "📏",
        category: "Queue Management"
      },
      {
        name: "nftQueueIsEmpty",
        title: "NFT Queue Is Empty",
        description: "Check if NFT queue is empty",
        inputs: [],
        icon: "❓",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "queueRequire",
        title: "Queue Require",
        description: "Get the queue requirement",
        inputs: [],
        icon: "📋",
        category: "Queue Management"
      },
      {
        name: "checkQueue",
        title: "Check Queue",
        description: "Check if NFT is in queue",
        inputs: [{ name: "tokenId", type: "uint256", placeholder: "Enter NFT token ID", required: true, label: "Token ID" }],
        icon: "🔍",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "getNftQueue",
        title: "Get NFT Queue",
        description: "Get the complete NFT queue as an array",
        inputs: [],
        icon: "📋",
        category: "Queue Management",
        formatOutput: (value) => value.join(', ') || 'Empty'
      },
      {
        key: "getNftQueueRange",
        name: "getNftQueue",
        title: "Get NFT Queue (Range)",
        description: "Get a range of the NFT queue by start/end index",
        inputs: [
          { name: "startIndex", type: "uint256", placeholder: "Start index", required: true, label: "Start Index" },
          { name: "endIndex", type: "uint256", placeholder: "End index", required: true, label: "End Index" }
        ],
        icon: "📋",
        category: "Queue Management",
        formatOutput: (value) => value.join(', ') || 'Empty'
      },
      {
        name: "getUserNftQueue",
        title: "Get User NFT Queue",
        description: "Get user NFT queue",
        inputs: [{ name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "📋",
        category: "Queue Management",
        formatOutput: (value) => value.join(', ') || 'Empty'
      },
      {
        name: "nftQueue",
        title: "NFT Queue At Index",
        description: "Get NFT queue at specific index",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "🔢",
        category: "Queue Management"
      },
      {
        name: "nftQueueDeleted",
        title: "NFT Queue Deleted",
        description: "Check if a queue slot is deleted",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "🗑️",
        category: "Queue Management",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "nftQueueTokenIndex",
        title: "NFT Queue Token Index",
        description: "Get the queue index of a token",
        inputs: [{ name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true, label: "Token ID" }],
        icon: "🔢",
        category: "Queue Management"
      },
      {
        name: "userNftQueue",
        title: "User NFT Queue At Index",
        description: "Get user NFT queue at index",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }
        ],
        icon: "🔢",
        category: "Queue Management"
      },
      // User Profile
      {
        name: "getUserAllNfts",
        title: "Get User All NFTs",
        description: "Get all NFTs owned by a user",
        inputs: [{ name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "🎨",
        category: "User Profile",
        formatOutput: (value) => value.join(', ') || 'None'
      },
      {
        name: "getUserHoldLength",
        title: "Get User Hold Length",
        description: "Get user hold length",
        inputs: [{ name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "📏",
        category: "User Profile"
      },
      {
        name: "getUserNextSale",
        title: "Get User Next Sale",
        description: "Get user next sale",
        inputs: [{ name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "📅",
        category: "User Profile"
      },
      {
        name: "getUserOwnedNftAt",
        title: "Get User Owned NFT At",
        description: "Get user's owned NFT at index",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }
        ],
        icon: "🔢",
        category: "User Profile"
      },
      {
        name: "getUserOwnedNftContains",
        title: "Get User Owned NFT Contains",
        description: "Check if user owns specific NFT",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" },
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true, label: "Token ID" }
        ],
        icon: "🔍",
        category: "User Profile",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "getUserOwnedNftLength",
        title: "Get User Owned NFT Length",
        description: "Get number of NFTs owned by user",
        inputs: [{ name: "account", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "📏",
        category: "User Profile"
      },
      {
        name: "lastPurchaseAt",
        title: "Last Purchase At",
        description: "Get last purchase timestamp for account",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "⏰",
        category: "User Profile"
      },
      {
        name: "userDayPurchase",
        title: "User Day Purchase",
        description: "Get user day purchase",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "day", type: "uint256", placeholder: "Enter day", required: true, label: "Day" }
        ],
        icon: "📅",
        category: "User Profile"
      },
      {
        name: "userProfiles",
        title: "User Profiles",
        description: "Get user profile information",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "👥",
        category: "User Profile",
        formatOutput: (value) => `Hold Start: ${value[0]}\nHold End: ${value[1]}\nLast Cycle: ${value[2]}\nLast Draw: ${value[3]}\nLast Purchase Timestamp: ${value[4]}\nExclude Condition: ${value[5] ? 'Yes' : 'No'}\nBlocked: ${value[6] ? 'Yes' : 'No'}`
      }
    ],

    writeFunctions: [
      // User Actions
      {
        name: "bulkNurnNftId",  // Matches ABI (bulk burn)
        title: "Bulk Burn NFT IDs",
        description: "Bulk burn NFT IDs",
        inputs: [{ name: "tokenIds", type: "uint256[]", placeholder: "Enter token IDs (comma separated)", required: true, label: "Token IDs", isArray: true }],
        icon: "🔥",
        category: "User Actions"
      },
      {
        name: "burnNftFromOwner",
        title: "Burn NFT From Owner",
        description: "Burn NFT from owner",
        inputs: [{ name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true, label: "NFT ID" }],
        icon: "🔥",
        category: "User Actions"
      },
      {
        name: "burnNftId",
        title: "Burn NFT ID",
        description: "Burn NFT by ID",
        inputs: [{ name: "nftTokenId", type: "uint256", placeholder: "Enter NFT token ID", required: true, label: "NFT Token ID" }],
        icon: "🔥",
        category: "User Actions"
      },
      {
        name: "buyNft",
        title: "Buy NFT",
        description: "Buy an NFT",
        inputs: [],
        requiresApproval: true,
        requiresBalanceCheck: true,
        icon: "🛒",
        category: "User Actions"
      },
      {
        name: "claimNft",
        title: "Claim NFT",
        description: "Claim NFT",
        inputs: [],
        icon: "🎁",
        category: "User Actions"
      },
      {
        name: "placeOrder",
        title: "Place Order",
        description: "Place order for NFT",
        inputs: [{ name: "nftId", type: "uint256", placeholder: "Enter NFT ID", required: true, label: "NFT ID" }],
        requiresApproval: true,
        requiresBalanceCheck: true,
        icon: "📝",
        category: "User Actions"
      },
      {
        name: "enableAutoTrade",
        title: "Enable Auto Trade",
        description: "Enable auto trade for your account",
        inputs: [],
        icon: "🤖",
        category: "User Actions"
      },
      {
        name: "compactQueue",
        title: "Compact Queue",
        description: "Compact the NFT queue (maintenance)",
        inputs: [{ name: "maxSteps", type: "uint256", placeholder: "Enter max steps", required: true, label: "Max Steps" }],
        icon: "🧹",
        category: "Queue Management"
      },
      {
        name: "onERC721Received",
        title: "On ERC721 Received",
        description: "ERC721 receiver callback (use with caution)",
        inputs: [
          { name: "operator", type: "address", placeholder: "Operator address", required: true, label: "Operator" },
          { name: "from", type: "address", placeholder: "From address", required: true, label: "From" },
          { name: "tokenId", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" },
          { name: "data", type: "bytes", placeholder: "Data (hex)", required: false, label: "Data" }
        ],
        icon: "🔄",
        category: "Advanced"
      }
    ],

    adminFunctions: [
      {
        name: "pause",
        title: "Pause",
        description: "Pause contract",
        inputs: [],
        icon: "⏸️",
        category: "Admin Control"
      },
      {
        name: "unpause",
        title: "Unpause",
        description: "Unpause contract",
        inputs: [],
        icon: "▶️",
        category: "Admin Control"
      },
      {
        name: "newSetUp",
        title: "New Set Up",
        description: "Run the contract new setup routine",
        inputs: [],
        icon: "🔧",
        category: "Admin Control"
      },
      {
        name: "renounceOwnership",
        title: "Renounce Ownership",
        description: "Renounce ownership",
        inputs: [],
        icon: "🚫",
        category: "Admin Control"
      },
      {
        name: "transferOwnership",
        title: "Transfer Ownership",
        description: "Transfer ownership",
        inputs: [{ name: "newOwner", type: "address", placeholder: "New owner", required: true, label: "New Owner" }],
        icon: "🔄",
        category: "Admin Control"
      },
      {
        name: "initialize",
        title: "Initialize",
        description: "Initialize the marketplace contract",
        inputs: [
          { name: "initialOwner", type: "address", placeholder: "Enter initial owner", required: true, label: "Initial Owner" },
          { name: "newSaleFundWallet", type: "address", placeholder: "Enter sale fund wallet", required: true, label: "Sale Fund Wallet" },
          { name: "usdtTokenAddress", type: "address", placeholder: "Enter USDT token address", required: true, label: "USDT Token Address" }
        ],
        icon: "🔧",
        category: "Admin Control"
      },
      {
        name: "recoverErc20",
        title: "Recover ERC20",
        description: "Recover ERC20 tokens",
        inputs: [
          { name: "erc20Token", type: "address", placeholder: "ERC20 token address", required: true, label: "ERC20 Token" },
          { name: "amount", type: "uint256", placeholder: "Amount", required: true, label: "Amount" }
        ],
        icon: "🔄",
        category: "Admin Recovery"
      },
      {
        name: "recoverEth",
        title: "Recover ETH",
        description: "Recover ETH",
        inputs: [{ name: "amount", type: "uint256", placeholder: "Amount", required: true, label: "Amount" }],
        icon: "💎",
        category: "Admin Recovery"
      },
      {
        name: "recoverNft",
        title: "Recover NFT",
        description: "Recover NFT to receiver",
        inputs: [
          { name: "receiver", type: "address", placeholder: "Receiver address", required: true, label: "Receiver" },
          { name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }
        ],
        icon: "🎨",
        category: "Admin Recovery"
      },
      {
        name: "setBurnAndMint",
        title: "Set Burn And Mint",
        description: "Set burn and mint parameters",
        inputs: [
          { name: "newMintNftCount", type: "uint256", placeholder: "New mint NFT count", required: true, label: "Mint Count" },
          { name: "newMaxSalePrice", type: "uint256", placeholder: "New max sale price", required: true, label: "Max Sale Price" }
        ],
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setClaimStatus",
        title: "Set Claim Status",
        description: "Set claim status",
        inputs: [{ name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }],
        icon: "✅",
        category: "Admin Settings"
      },
      {
        name: "setGlobalPeriod",
        title: "Set Global Period",
        description: "Set global period",
        inputs: [{ name: "newGlobalPeriod", type: "uint256", placeholder: "New global period", required: true, label: "Global Period" }],
        icon: "⏱️",
        category: "Admin Settings"
      },
      {
        name: "setLastPurchaseAt",
        title: "Set Last Purchase At",
        description: "Set last purchase timestamp",
        inputs: [
          { name: "account", type: "address", placeholder: "Account address", required: true, label: "Account" },
          { name: "newPeriod", type: "uint256", placeholder: "New period", required: true, label: "Period" }
        ],
        icon: "⏰",
        category: "Admin Settings"
      },
      {
        name: "setMaxNftSupply",
        title: "Set Max NFT Supply",
        description: "Set max NFT supply",
        inputs: [{ name: "newSupply", type: "uint256", placeholder: "New supply", required: true, label: "Supply" }],
        icon: "📈",
        category: "Admin Settings"
      },
      {
        name: "setMaxSalePrice",
        title: "Set Max Sale Price",
        description: "Set max sale price",
        inputs: [{ name: "newMaxSalePrice", type: "uint256", placeholder: "New max sale price", required: true, label: "Max Sale Price" }],
        icon: "💲",
        category: "Admin Settings"
      },
      {
        name: "setNftBurnBasePrice",
        title: "Set NFT Burn Base Price",
        description: "Set NFT burn base price",
        inputs: [{ name: "newNftBurnBasePrice", type: "uint256", placeholder: "New burn base price", required: true, label: "Burn Base Price" }],
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setNftBurnRewardContract",
        title: "Set NFT Burn Reward Contract",
        description: "Set NFT burn reward contract address",
        inputs: [{ name: "newNftReward", type: "address", placeholder: "New burn reward address", required: true, label: "Burn Reward Address" }],
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setNftContract",
        title: "Set NFT Contract",
        description: "Set NFT contract address",
        inputs: [{ name: "newNftAddress", type: "address", placeholder: "New NFT address", required: true, label: "NFT Address" }],
        icon: "🎨",
        category: "Admin Settings"
      },
      {
        name: "setNftMatrixContract",
        title: "Set NFT Matrix Contract",
        description: "Set NFT matrix contract",
        inputs: [{ name: "newNftMatrix", type: "address", placeholder: "New matrix address", required: true, label: "Matrix Address" }],
        icon: "🤖",
        category: "Admin Settings"
      },
      {
        name: "setNftPrice",
        title: "Set NFT Price",
        description: "Set NFT base price",
        inputs: [{ name: "newPrice", type: "uint256", placeholder: "New price", required: true, label: "Price" }],
        icon: "💵",
        category: "Admin Settings"
      },
      {
        name: "setNftSaleDuration",
        title: "Set NFT Sale Duration",
        description: "Set NFT sale duration",
        inputs: [{ name: "newNftSaleDuration", type: "uint256", placeholder: "New sale duration", required: true, label: "Sale Duration" }],
        icon: "⏱️",
        category: "Admin Settings"
      },
      {
        name: "setPlatformRewardReceiver",
        title: "Set Platform Reward Receiver",
        description: "Set platform reward receiver and burn fee receiver",
        inputs: [
          { name: "newPlatformRewardReceiver", type: "address", placeholder: "New platform receiver", required: true, label: "Platform Receiver" },
          { name: "newBurnFeeReceiver", type: "address", placeholder: "New burn fee receiver", required: true, label: "Burn Fee Receiver" }
        ],
        icon: "📤",
        category: "Admin Settings"
      },
      {
        name: "setQueueRequire",
        title: "Set Queue Require",
        description: "Set queue requirement",
        inputs: [{ name: "newQueueRequire", type: "uint256", placeholder: "New require", required: true, label: "Require" }],
        icon: "📋",
        category: "Admin Settings"
      },
      {
        name: "setSaleIncomePercent",
        title: "Set Sale Income Percent",
        description: "Set sale income percentages",
        inputs: [
          { name: "newNftOwnerIncomePercent", type: "uint256", placeholder: "Owner %", required: true, label: "Owner %" },
          { name: "newLevelIncomePercent", type: "uint256", placeholder: "Level %", required: true, label: "Level %" },
          { name: "newBuringIncomePercent", type: "uint256", placeholder: "Burning %", required: true, label: "Burning %" },
          { name: "newPlatformIncomePercent", type: "uint256", placeholder: "Platform %", required: true, label: "Platform %" }
        ],
        icon: "📊",
        category: "Admin Settings"
      },
      {
        name: "setSellPercent",
        title: "Set Sell Percent",
        description: "Set sell percent",
        inputs: [{ name: "newSellPercent", type: "uint256", placeholder: "New sell %", required: true, label: "Sell %" }],
        icon: "📉",
        category: "Admin Settings"
      },
      {
        name: "setTopIdWallet",
        title: "Set Top ID Wallet",
        description: "Set top ID wallet and NFT sale fund wallet",
        inputs: [
          { name: "newTopIdWallet", type: "address", placeholder: "New top ID wallet", required: true, label: "Top ID Wallet" },
          { name: "newNftSaleFundWallet", type: "address", placeholder: "New NFT sale fund wallet", required: true, label: "NFT Sale Fund Wallet" }
        ],
        icon: "🏆",
        category: "Admin Settings"
      },
      {
        name: "setUsdtToken",
        title: "Set USDT Token",
        description: "Set USDT token address",
        inputs: [{ name: "newUsdtToken", type: "address", placeholder: "New USDT address", required: true, label: "USDT Address" }],
        icon: "💰",
        category: "Admin Settings"
      },
      {
        name: "setUserBlockRewardCondition",
        title: "Set User Block Reward Condition",
        description: "Set user block reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "User account", required: true, label: "User Account" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }
        ],
        icon: "🚫",
        category: "Admin Settings"
      },
      {
        name: "setUserExcludeRewardCondition",
        title: "Set User Exclude Reward Condition",
        description: "Set user exclude reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "User account", required: true, label: "User Account" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }
        ],
        icon: "❌",
        category: "Admin Settings"
      },
      {
        name: "transferNftToOwner",
        title: "Transfer NFT To Owner",
        description: "Transfer NFTs to owner (range)",
        inputs: [
          { name: "start", type: "uint256", placeholder: "Start index", required: true, label: "Start" },
          { name: "end", type: "uint256", placeholder: "End index", required: true, label: "End" }
        ],
        icon: "📤",
        category: "Admin Transfer"
      },
      {
        name: "transferNftToUser",
        title: "Transfer NFT To User",
        description: "Transfer NFTs to users",
        inputs: [
          { name: "accounts", type: "address[]", placeholder: "Addresses (comma separated)", required: true, label: "Accounts", isArray: true },
          { name: "tokenIds", type: "uint256[]", placeholder: "Token IDs (comma separated)", required: true, label: "Token IDs", isArray: true }
        ],
        icon: "📤",
        category: "Admin Transfer"
      },
      // Auto Trade Functions
      {
        name: "autoTradeBatch",
        title: "Auto Trade Batch",
        description: "Execute auto trade batch for the given tokens and bots",
        inputs: [
          { name: "tokenIds", type: "uint256[]", placeholder: "Token IDs (comma separated)", required: true, label: "Token IDs", isArray: true },
          { name: "bots", type: "address[]", placeholder: "Bot addresses (comma separated)", required: true, label: "Bots", isArray: true }
        ],
        icon: "🤖",
        category: "Auto Trade"
      },
      {
        name: "setAutoTradeFee",
        title: "Set Auto Trade Fee",
        description: "Set the auto trade fee amount",
        inputs: [{ name: "newFee", type: "uint256", placeholder: "Enter new fee", required: true, label: "New Fee" }],
        icon: "💸",
        category: "Auto Trade Settings"
      },
      {
        name: "setAutoTradeFeeEnable",
        title: "Set Auto Trade Fee Enable",
        description: "Enable or disable the auto trade fee",
        inputs: [{ name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }],
        icon: "🔀",
        category: "Auto Trade Settings"
      },
      {
        name: "setAutoTradeFeeReceiver",
        title: "Set Auto Trade Fee Receiver",
        description: "Set the auto trade fee receiver address",
        inputs: [{ name: "newWallet", type: "address", placeholder: "Enter wallet address", required: true, label: "Wallet Address" }],
        icon: "📥",
        category: "Auto Trade Settings"
      },
      {
        name: "setBotTimeDuration",
        title: "Set Bot Time Duration",
        description: "Set the bot time duration",
        inputs: [{ name: "newDuration", type: "uint256", placeholder: "Enter new duration", required: true, label: "New Duration" }],
        icon: "⏱️",
        category: "Auto Trade Settings"
      },
    ]
  };

  const connectWallet = async () => {
    setLoadingStates(prev => ({ ...prev, connect: true }));
    
    try {
      const acc = await loadWeb3();
      if (acc === "No Wallet") {
        toast.error("No Wallet Detected");
        setBtnTxt("Connect Wallet");
      } else if (acc === "Wrong Network") {
        toast.error("Please switch to opBNB Mainnet");
        setBtnTxt("Wrong Network");
      } else {
        setAccount(acc);
        setBtnTxt(acc.substring(0, 6) + '...' + acc.substring(acc.length - 4));
        toast.success("Connected to opBNB Mainnet");
        
        loadMarketplaceData();
      }
    } catch (error) {
      console.error("Connection error:", error);
      toast.error("Connection failed");
      setBtnTxt("Connect Wallet");
    } finally {
      setLoadingStates(prev => ({ ...prev, connect: false }));
    }
  };

  const getContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(AxonftMarketplaceAbi, AxonftMarketplace);
  };

  const getUsdtContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(USDTAbi, USDT);
  };

  // Load marketplace overview data
  const loadMarketplaceData = useCallback(async () => {
    if (!account) return;
    
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      
      const [totalNftsMinted, maxNftSupply, nftBasePrice, nftQueueLength, isClaimEnable, contractUsdtBalance, userUsdtBalance] = await Promise.all([
        contract.methods.totalNftsMinted().call().catch(() => "0"),
        contract.methods.maxNftSupply().call().catch(() => "0"),
        contract.methods.nftBasePrice().call().catch(() => "0"),
        contract.methods.nftQueueLength().call().catch(() => "0"),
        contract.methods.isClaimEnable().call().catch(() => false),
        usdtContract.methods.balanceOf(AxonftMarketplace).call().catch(() => "0"),
        usdtContract.methods.balanceOf(account).call().catch(() => "0")
      ]);

      setMarketplaceData({
        totalNftsMinted: totalNftsMinted.toString(),
        maxNftSupply: maxNftSupply.toString(),
        nftBasePrice: web3.utils.fromWei(nftBasePrice.toString(), 'ether'),
        queueLength: nftQueueLength.toString(),
        isClaimEnabled: isClaimEnable,
        usdtBalance: web3.utils.fromWei(contractUsdtBalance.toString(), 'ether'),
        userUsdtBalance: web3.utils.fromWei(userUsdtBalance.toString(), 'ether')
      });
      
    } catch (error) {
      console.error("Error loading marketplace data:", error);
      toast.error("Failed to load marketplace data");
    }
  }, [account]);

  useEffect(() => {
    if (account) {
      loadMarketplaceData();
    }
  }, [account, loadMarketplaceData]);

  // Handle input changes
  const handleInputChange = (functionName, inputName, value) => {
    setFunctionInputs(prev => ({
      ...prev,
      [functionName]: {
        ...prev[functionName],
        [inputName]: value
      }
    }));
  };

  // Validate inputs based on type
  const validateInput = (type, value) => {
    if (type === 'address' && !getWeb3Instance().utils.isAddress(value)) {
      throw new Error('Invalid address');
    }
    if (type.startsWith('uint') && (isNaN(value) || Number(value) < 0)) {
      throw new Error('Invalid uint value');
    }
    if (type === 'bool' && !['true', 'false'].includes(value.toLowerCase())) {
      throw new Error('Invalid bool value');
    }
    if (type === 'bytes' && !value.startsWith('0x')) {
      throw new Error('Bytes should start with 0x');
    }
    return true;
  };

  // Execute read functions
  const executeReadFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.key || functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const contract = getContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        let value = inputs[input.name] || '';
        if (input.required && value.trim() === '') {
          throw new Error(`${input.label} is required`);
        }
        if (input.isArray && typeof value === 'string') {
          value = value.split(',').map(v => v.trim());
          value.forEach(v => validateInput(input.type.replace('[]', ''), v));
          return value;
        }
        validateInput(input.type, value);
        return value;
      });

      let result = await contract.methods[functionData.name](...args).call({ from: account });

      // Handle BigInt in result
      const convertBigInt = (val) => {
        if (typeof val === 'bigint') return val.toString();
        if (typeof val === 'object' && val !== null) {
          if (Array.isArray(val)) return val.map(convertBigInt);
          const obj = {};
          Object.keys(val).forEach(key => { obj[key] = convertBigInt(val[key]); });
          return obj;
        }
        return val;
      };
      result = convertBigInt(result);

      const formattedResult = functionData.formatOutput 
        ? functionData.formatOutput(result)
        : JSON.stringify(result, (_, v) => typeof v === 'bigint' ? v.toString() : v, 2);

      setFunctionOutputs(prev => ({
        ...prev,
        [funcKey]: formattedResult
      }));

      toast.success("Read operation successful!");
    } catch (error) {
      console.error("Read error:", error);
      toast.error(error.message || "Read operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Execute write functions with simulation and checks
  const executeWriteFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.key || functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        let value = inputs[input.name] || '';
        if (input.required && value.trim() === '') {
          throw new Error(`${input.label} is required`);
        }
        if (input.isArray && typeof value === 'string') {
          value = value.split(',').map(v => v.trim());
          value.forEach(v => validateInput(input.type.replace('[]', ''), v));
          return value;
        }
        validateInput(input.type, value);
        if (input.type === 'bool') return value.toLowerCase() === 'true';
        if (input.type.startsWith('uint')) return value;
        return value;
      });

      // Check if paused
      const isPaused = await contract.methods.paused().call();
      if (isPaused && !['unpause'].includes(functionData.name)) {
        throw new Error('Contract is paused');
      }

      // Check if owner for admin functions
      if (functionData.category.startsWith('Admin')) {
        const owner = await contract.methods.owner().call();
        if (owner.toLowerCase() !== account.toLowerCase()) {
          throw new Error('Only owner can call this');
        }
      }

      // Balance and approval check for functions that need USDT
      if (functionData.requiresBalanceCheck || functionData.requiresApproval) {
        let requiredAmount = '0';
        if (functionData.name === 'buyNft') {
          requiredAmount = await contract.methods.nftBasePrice().call();
        } else if (functionData.name === 'placeOrder') {
          const nftId = args[0];
          requiredAmount = await contract.methods.nftSalePrice(nftId).call();
        } // Add more if needed for other functions

        const userBalance = await usdtContract.methods.balanceOf(account).call();
        if (Number(userBalance) < Number(requiredAmount)) {
          throw new Error('Insufficient USDT balance');
        }

        if (functionData.requiresApproval) {
          const allowance = await usdtContract.methods.allowance(account, AxonftMarketplace).call();
          if (Number(allowance) < Number(requiredAmount)) {
            toast.info("Approving USDT...");
            const approveGas = await usdtContract.methods.approve(AxonftMarketplace, requiredAmount).estimateGas({ from: account });
            await usdtContract.methods.approve(AxonftMarketplace, requiredAmount).send({
              from: account,
              gas: Math.floor(Number(approveGas) * 1.5)
            });
            toast.success("USDT Approved!");
          }
        }
      }

      // Simulate transaction to catch revert reasons
      try {
        await contract.methods[functionData.name](...args).call({ from: account });
      } catch (simError) {
        // Extract revert reason from different error formats
        let revertReason = 'Unknown reason';
        if (simError?.data?.message) {
          revertReason = simError.data.message;
        } else if (simError?.message) {
          // Strip generic RPC wrapper to show actual revert reason
          const match = simError.message.match(/revert\s+(.*)/i) || simError.message.match(/reason:\s*(.*)/i);
          revertReason = match ? match[1].trim() : simError.message;
        }
        console.error("Simulation error details:", simError);
        throw new Error(`Transaction would revert: ${revertReason}`);
      }

      // Estimate gas with buffer
      let gasEstimate;
      try {
        gasEstimate = await contract.methods[functionData.name](...args).estimateGas({ from: account });
      } catch (estError) {
        throw new Error(`Gas estimation failed: ${estError.message || 'Likely reversion'}`);
      }
      const gasWithBuffer = Math.floor(Number(gasEstimate) * 1.5);

      // Send transaction
      const tx = await contract.methods[functionData.name](...args).send({
        from: account,
        gas: gasWithBuffer
      });

      toast.success(`Transaction successful! Hash: ${tx.transactionHash}`);
      loadMarketplaceData();
    } catch (error) {
      console.error("Write error:", error);
      toast.error(error.message || "Write operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Group functions by category
  const groupFunctionsByCategory = (functions) => {
    return functions.reduce((acc, func) => {
      const cat = func.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(func);
      return acc;
    }, {});
  };

  // Render function card
  const renderFunctionCard = (func, isWrite = false) => {
    const funcKey = func.key || func.name;
    const inputs = functionInputs[funcKey] || {};
    const output = functionOutputs[funcKey];
    const isLoading = loadingStates[funcKey] || false;

    return (
      <div key={funcKey} className="col-lg-4 col-md-6 mb-4">
        <div className="card h-100 shadow-lg border-0" style={{
          borderRadius: '20px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="card-body p-4 d-flex flex-column">
            <div className="d-flex align-items-center mb-3">
              <span className="fs-3 me-3">{func.icon}</span>
              <div>
                <h5 className="mb-1 text-white">{func.title}</h5>
                <small className="text-white-50">{func.description}</small>
              </div>
            </div>

            {func.inputs.map(input => (
              <div key={input.name} className="mb-3">
                <label className="form-label text-white-50 small mb-1">
                  {input.label || input.name}
                  {input.required && <span className="text-danger">*</span>}
                </label>
                <input
                  type="text"
                  className="form-control bg-dark border-0 text-white placeholder-white"
                  placeholder={input.placeholder}
                  value={inputs[input.name] || ''}
                  onChange={(e) => handleInputChange(funcKey, input.name, e.target.value)}
                  style={{
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '0.9rem'
                  }}
                />
                {input.isArray && (
                  <small className="text-white-50 d-block mt-1">
                    Separate multiple values with commas
                  </small>
                )}
              </div>
            ))}

            <button 
              onClick={() => isWrite 
                ? executeWriteFunction(func)
                : executeReadFunction(func)}
              disabled={isLoading}
              className="btn w-100 mt-auto"
              style={{
                background: isWrite 
                  ? 'linear-gradient(135deg, #ff6b6b, #4ecdc4)' 
                  : 'linear-gradient(135deg, #00ff88, #00ccff)',
                border: 'none',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: '600'
              }}
            >
              {isLoading ? 'Processing...' : (isWrite ? 'Execute Write' : 'Read')}
            </button>

            {output && (
              <div className="mt-3 p-3 rounded-3" style={{
                background: 'rgba(0, 255, 136, 0.1)',
                border: '1px solid rgba(0, 255, 136, 0.3)',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <small className="text-white-50 d-block mb-2">Output:</small>
                <code className="text-white">{output}</code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-4" style={{
      background: 'linear-gradient(135deg, #000428, #004e92)'
    }}>
      <div className="container text-center">
        <h1 className="mb-5 display-4 fw-bold" style={{
          background: 'linear-gradient(135deg, #00ff88, #00ccff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Axo NFT Marketplace Dashboard
        </h1>

        <div className="mb-5">
          <button 
            onClick={connectWallet}
            disabled={loadingStates.connect}
            className="btn px-4 px-md-5 py-3 rounded-pill shadow-lg position-relative overflow-hidden"
            style={{ 
              background: account ? 
                'linear-gradient(135deg, #00ff88, #00ccff)' : 
                'linear-gradient(135deg, #ff6b6b, #4ecdc4)',
              border: 'none',
              color: account ? '#000' : '#fff',
              fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', 
              fontWeight: '700',
              transition: 'all 0.3s ease',
              minWidth: '160px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              opacity: loadingStates.connect ? 0.7 : 1
            }}
          >
            {loadingStates.connect ? (
              <div className="d-flex align-items-center justify-content-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Connecting...
              </div>
            ) : (
              <span className="position-relative">{btnTxt}</span>
            )}
          </button>
        </div>
        
        {account && (
          <div className="mt-4 p-3 rounded-4" style={{
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)'
          }}>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <span className="text-white-50 small">
                🔗 {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </span>
              <span className="badge bg-success rounded-pill">Connected</span>
            </div>
          </div>
        )}

        {account && (
          <div className="row">
            <div className="col-12">
              <div className="card shadow-lg border-0 mb-4" style={{ 
                borderRadius: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="card-header border-0 p-4" style={{ 
                  borderRadius: '24px 24px 0 0',
                  background: 'transparent'
                }}>
                  <ul className="nav nav-pills justify-content-center flex-wrap gap-2" role="tablist">
                    {[
                      { key: 'overview', label: 'Overview', icon: '🏪', color: '#00ff88' },
                      { key: 'read', label: 'Read Functions', icon: '👁️', color: '#45b7d1' },
                      { key: 'write', label: 'Write Functions', icon: '📝', color: '#4ecdc4' },
                      { key: 'admin', label: 'Admin Functions', icon: '⚙️', color: '#ff6b6b' }
                    ].map(tab => (
                      <li key={tab.key} className="nav-item">
                        <button
                          className={`nav-link border-0 px-4 py-3 fw-bold position-relative overflow-hidden ${
                            activeTab === tab.key ? 'text-dark' : 'text-white-50'
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                          style={{
                            borderRadius: '16px',
                            background: activeTab === tab.key ? 
                              `linear-gradient(135deg, ${tab.color}, ${tab.color}80)` : 
                              'rgba(255, 255, 255, 0.05)',
                            border: activeTab === tab.key ? 
                              `1px solid ${tab.color}` : 
                              '1px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: activeTab === tab.key ? 'translateY(-2px)' : 'translateY(0)',
                            boxShadow: activeTab === tab.key ? 
                              `0 8px 25px ${tab.color}40` : 'none'
                          }}
                        >
                          <span className="me-2 fs-5">{tab.icon}</span>
                          <span className="d-none d-md-inline">{tab.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body p-4" style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '0 0 24px 24px'
                }}>
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="row g-4">
                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">🎨</div>
                            <h4 className="text-white mb-2">{marketplaceData.totalNftsMinted || '0'}</h4>
                            <p className="text-white-50 mb-0">Total Minted</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(marketplaceFunctions.readFunctions.find(f => f.name === 'totalNftsMinted'))}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.15), rgba(107, 224, 215, 0.1))',
                          border: '1px solid rgba(69, 183, 209, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">📈</div>
                            <h4 className="text-white mb-2">{marketplaceData.maxNftSupply || '0'}</h4>
                            <p className="text-white-50 mb-0">Max Supply</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(marketplaceFunctions.readFunctions.find(f => f.name === 'maxNftSupply'))}
                              style={{
                                background: 'rgba(69, 183, 209, 0.2)',
                                border: '1px solid #45b7d1',
                                color: '#45b7d1'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(107, 224, 215, 0.15), rgba(255, 170, 0, 0.1))',
                          border: '1px solid rgba(107, 224, 215, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💵</div>
                            <h4 className="text-white mb-2">{marketplaceData.nftBasePrice || '0'} USDT</h4>
                            <p className="text-white-50 mb-0">Base Price</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(marketplaceFunctions.readFunctions.find(f => f.name === 'nftBasePrice'))}
                              style={{
                                background: 'rgba(107, 224, 215, 0.2)',
                                border: '1px solid #6be0d7',
                                color: '#6be0d7'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(255, 170, 0, 0.15), rgba(255, 107, 107, 0.1))',
                          border: '1px solid rgba(255, 170, 0, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">📋</div>
                            <h4 className="text-white mb-2">{marketplaceData.queueLength || '0'}</h4>
                            <p className="text-white-50 mb-0">Queue Length</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(marketplaceFunctions.readFunctions.find(f => f.name === 'nftQueueLength'))}
                              style={{
                                background: 'rgba(255, 170, 0, 0.2)',
                                border: '1px solid #ffaa00',
                                color: '#ffaa00'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${marketplaceData.isClaimEnabled ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 107, 107, 0.15)'}, rgba(255, 170, 0, 0.1))`,
                          border: `1px solid ${marketplaceData.isClaimEnabled ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`,
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">{marketplaceData.isClaimEnabled ? '✅' : '❌'}</div>
                            <h4 className="text-white mb-2">
                              {marketplaceData.isClaimEnabled ? 'ENABLED' : 'DISABLED'}
                            </h4>
                            <p className="text-white-50 mb-0">Claim Status</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(marketplaceFunctions.readFunctions.find(f => f.name === 'isClaimEnable'))}
                              style={{
                                background: marketplaceData.isClaimEnabled ? 
                                  'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 107, 0.2)',
                                border: marketplaceData.isClaimEnabled ? 
                                  '1px solid #00ff88' : '1px solid #ff6b6b',
                                color: marketplaceData.isClaimEnabled ? '#00ff88' : '#ff6b6b'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(255, 170, 0, 0.15), rgba(255, 107, 107, 0.1))',
                          border: '1px solid rgba(255, 170, 0, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💰</div>
                            <h4 className="text-white mb-2">{marketplaceData.usdtBalance || '0'} USDT</h4>
                            <p className="text-white-50 mb-0">Contract Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMarketplaceData}
                              style={{
                                background: 'rgba(255, 170, 0, 0.2)',
                                border: '1px solid #ffaa00',
                                color: '#ffaa00'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💼</div>
                            <h4 className="text-white mb-2">{marketplaceData.userUsdtBalance || '0'} USDT</h4>
                            <p className="text-white-50 mb-0">Your Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMarketplaceData}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Read Functions Tab */}
                  {activeTab === 'read' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 text-info">👁️ Read Functions</h4>
                        <small className="text-white-50">
                          {marketplaceFunctions.readFunctions.length} functions
                        </small>
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.readFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50 d-flex align-items-center">
                            <span className="me-2">{category}</span>
                            <span className="badge bg-secondary rounded-pill">{funcs.length}</span>
                          </h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, false))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Write Functions Tab */}
                  {activeTab === 'write' && (
                    <div>
                      <h4 className="mb-4" style={{color: '#4ecdc4'}}>📝 Write Functions</h4>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.writeFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin Functions Tab */}
                  {activeTab === 'admin' && (
                    <div>
                      <h4 className="mb-4" style={{color: '#ff6b6b'}}>⚙️ Admin Functions</h4>
                      <div className="alert" style={{
                        background: 'rgba(255, 107, 107, 0.1)',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                        borderRadius: '12px',
                        color: '#ff6b6b'
                      }}>
                        ⚠️ <strong>Warning:</strong> These functions are restricted to the contract owner/admin. Unauthorized calls will be reverted.
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.adminFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}